import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useContext,
} from "react";
import { Modal, Form, Input, Checkbox, Space, Select, message } from "antd";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import styled, { css, createGlobalStyle } from "styled-components";
import Button from "./Button";
import { APP_NAME } from "../helpers/constants";
import { Spacer } from "../styles/CommonElements";
import { trackEvent } from "../helpers/mixpanel";
import { discordTrack } from "../lib/analytics/discordTrack";
import { APP_TERMS_ROUTE } from "../helpers/routeNames";
import { getPostLoginHomePath } from "../helpers/accountProduct";
import {
  getPricingScheduleWithFree,
  formatMonthlyUsd,
  getAnnualDisplayUsd,
  formatAnnualDisplayUsd,
  getHomePricingGridPlanOrder,
  HOME_PRICING_PLAN_COLUMN_COPY,
  getCheckoutPlanKeyForStripe,
} from "../lib/pricing";
import {
  getProcurementIntakePlanOptions,
  getProcurementSummaryPriceParts,
  PROCUREMENT_TIER_DETAIL,
} from "../lib/procurementPricing";
import {
  submitFreeGatewaySignup,
  FREE_GATEWAY_EMAIL_IN_USE,
} from "../lib/api/freeGatewaySignup";
import { AuthContext } from "../context/AuthProvider";
import SystemDiagnostic from "./onboarding/SystemDiagnostic";
import Logo from "./general/Logo";

const { TextArea } = Input;
const { Option } = Select;

// Legacy alias: older bundles or half-merged files may still reference `<PhoneInput />` after
// the phone field was removed; a plain `Input` avoids ReferenceError. Safe to delete once
// no usage remains anywhere in the tree.
// eslint-disable-next-line no-unused-vars -- may be used only in some build/HMR states
const PhoneInput = Input;

/** Industrial dark shell for intake / marketing signup modals */
const INTAKE_DARK_PANEL = "#0f172a";
const INTAKE_DARK_DEEP = "#020617";
const INTAKE_DARK_CARD = "#1e293b";
const INTAKE_DARK_BORDER = "#334155";
const INTAKE_DARK_TEXT = "#f1f5f9";
const INTAKE_DARK_MUTED = "#94a3b8";
/** Form labels on dark marketing intake: must beat Ant light-theme token overrides */
const INTAKE_MARKETING_LABEL = "#e2e8f0";

/** Checkout billing cycle is chosen in-modal (or seeded from the pricing page). */

/** Compact price for plan tab labels (marketing signup). */
function formatPlanTabPrice(
  opt,
  billingInterval,
  pricingScheduleGroup,
  isProcurementProduct
) {
  if (!opt) return "";
  if (isProcurementProduct) return opt.tabPrice || "";
  if (opt.price === 0) return "Free";
  if (billingInterval === "annual") {
    const annualUsd = getAnnualDisplayUsd(opt.value, pricingScheduleGroup);
    if (annualUsd != null) {
      return `$${formatAnnualDisplayUsd(annualUsd)}/yr`;
    }
  }
  return `$${formatMonthlyUsd(opt.price)}/mo`;
}

/** Large price + suffix for selected-plan summary card. */
function getPlanPriceParts(
  opt,
  billingInterval,
  pricingScheduleGroup,
  isProcurementProduct
) {
  if (isProcurementProduct && opt?.value) {
    return getProcurementSummaryPriceParts(opt.value);
  }
  if (!opt || opt.price === 0) {
    return { dollars: "Free", period: "" };
  }
  if (billingInterval === "annual") {
    const annualUsd = getAnnualDisplayUsd(opt.value, pricingScheduleGroup);
    if (annualUsd != null) {
      return {
        dollars: `$${formatAnnualDisplayUsd(annualUsd)}`,
        period: "/year",
      };
    }
  }
  return {
    dollars: `$${formatMonthlyUsd(opt.price)}`,
    period: "/month",
  };
}

function normalizeInitialPlanForProduct(initialPlan, pricingProduct) {
  // Strategic pivot: Everyone is individual (pay-as-you-go) now
  return "individual";
}

function buildIntakePlanOptions(schedule, pricingTierGroup) {
  const keys = Object.keys(schedule).filter(
    (k) => k !== "enterprise" && k !== "business"
  );
  const order = getHomePricingGridPlanOrder(pricingTierGroup);
  const sortedKeys = [
    ...order.filter((k) => keys.includes(k)),
    ...keys.filter((k) => !order.includes(k)),
  ];
  return sortedKeys
    .map((key) => {
      const tier = schedule[key];
      if (!tier) return null;
      const marketingLabel =
        HOME_PRICING_PLAN_COLUMN_COPY[key]?.headline || tier.name || key;
      return {
        label: tier.name || key,
        marketingLabel,
        value: key,
        price: tier.monthlyUSD,
        units: tier.unitCap,
      };
    })
    .filter(Boolean);
}

/**
 * Normalize a plan key for UI display (removes chat_ prefix so the suite plan
 * options in the dropdown match). Does NOT change what's sent to the backend.
 */
function normalizePlanForUnifiedCheckout(plan) {
  const raw = String(plan || "")
    .trim()
    .toLowerCase();
  if (raw === "trial") return "trial";
  if (raw === "free") return "free";
  if (raw.startsWith("chat_")) {
    const map = {
      chat_starter: "starter",
      chat_growth: "growth",
      chat_scale: "scale",
      chat_pro: "growth",
      chat_business: "growth",
    };
    return map[raw] || "starter";
  }
  if (raw === "business") return "scale";
  if (
    raw === "starter" ||
    raw === "growth" ||
    raw === "scale" ||
    raw === "pro"
  ) {
    return raw;
  }
  /** Enterprise is quote-based only; closest self-serve tier for intake preselect. */
  if (raw === "enterprise") return "scale";
  return "starter";
}

/**
 * Map procurement intake plan tabs to Stripe checkout plan keys.
 */
function procurementCheckoutPlanKey(displayPlan) {
  const p = String(displayPlan || "")
    .trim()
    .toLowerCase();
  if (p === "portfolio" || p === "professional") {
    return "procurement_professional";
  }
  if (p === "institutional") {
    return "procurement_institutional";
  }
  return null;
}

/**
 * Return the plan key to send to the backend / Stripe.
 * Triage credit tiers (portfolio / institutional) must map to procurement_* SKUs,
 * never legacy chat_starter.
 */
function deriveCheckoutPlanKey(displayPlan) {
  const procurementKey = procurementCheckoutPlanKey(displayPlan);
  if (procurementKey) return procurementKey;

  const p = String(displayPlan || "")
    .trim()
    .toLowerCase();
  // Already a chat_ key (e.g. if passed through unchanged)
  if (p === "chat_starter" || p === "chat_pro") return "chat_starter";
  if (p === "chat_growth" || p === "chat_business") return "chat_growth";
  if (p === "chat_scale") return "scale";
  // Suite display keys
  if (p === "starter") return "chat_starter";
  if (p === "growth" || p === "pro") return "chat_growth";
  if (p === "business" || p === "scale") return "scale";
  return "chat_starter";
}

/** Stripe `selectedPlan` for intake checkout (procurement + legacy SaaS). */
function resolveIntakeCheckoutPlanKey(displayPlan, isProcurementProduct) {
  if (isProcurementProduct) {
    return (
      procurementCheckoutPlanKey(displayPlan) ||
      getCheckoutPlanKeyForStripe(displayPlan)
    );
  }
  return deriveCheckoutPlanKey(displayPlan);
}

/** Product line label in the plan dropdown + modal header. */
function planBrandPrefix() {
  return "Clumse";
}

/** Inset from viewport edges (safe areas + fixed margin) so the intake never hugs top/bottom. */
const IntakeModalWrapGlobal = createGlobalStyle`
  /* globals.css uses body.ant-modal-wrap with overflow-y auto so tall dialogs scroll
     inside the wrap; that beat our single-class intake rule. Chain both classes with
     the same body prefix so outer scroll stays off and only .ant-modal-body scrolls. */
  body .ant-modal-wrap.intake-modal-wrap,
  body > .ant-modal-wrap.intake-modal-wrap,
  #root .ant-modal-wrap.intake-modal-wrap,
  div.ant-modal-wrap.intake-modal-wrap,
  body .ant-modal-root.clumse-intake-modal .ant-modal-wrap,
  #root .ant-modal-root.clumse-intake-modal .ant-modal-wrap {
    overflow-x: hidden !important;
    overflow-y: hidden !important;
    overscroll-behavior: contain;
  }

  .intake-modal-wrap {
    padding: max(1.25rem, env(safe-area-inset-top, 0px))
      max(1rem, env(safe-area-inset-right, 0px))
      max(1.25rem, env(safe-area-inset-bottom, 0px))
      max(1rem, env(safe-area-inset-left, 0px)) !important;
    box-sizing: border-box;
  }

  @media (min-width: 769px) {
    .intake-modal-wrap {
      padding: max(1.5rem, env(safe-area-inset-top, 0px))
        max(1.5rem, env(safe-area-inset-right, 0px))
        max(1.5rem, env(safe-area-inset-bottom, 0px))
        max(1.5rem, env(safe-area-inset-left, 0px)) !important;
    }
  }

  /* Desktop intake width; always capped to viewport */
  .intake-modal-wrap .ant-modal {
    top: 0 !important;
    margin: 0 auto;
    padding-bottom: 0;
    width: 100% !important;
    max-width: min(778px, calc(100vw - 2rem)) !important;
    /* Wrap padding (top+bottom) + safe areas; 3.5rem ≈ mobile 2×1.25 + buffer */
    max-height: calc(
      100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 3.5rem
    );
  }

  .intake-modal-wrap.intake-modal-wrap--marketing .ant-modal {
    max-width: min(480px, calc(100vw - 2rem)) !important;
  }

  @media (max-width: 400px) {
    .intake-modal-wrap .ant-modal {
      max-width: calc(100vw - 1.5rem) !important;
    }
  }

  .intake-modal-wrap .ant-modal-content {
    max-height: calc(
      100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 3.5rem
    ) !important;
    display: flex !important;
    flex-direction: column !important;
    overflow: hidden !important;
  }

  /* Beat globals.css body .ant-modal-content max-height:none on the intake dialog */
  body .ant-modal-wrap.intake-modal-wrap .ant-modal-content {
    position: relative !important;
    max-height: calc(
      100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 3.5rem
    ) !important;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
  }

  .intake-modal-wrap .ant-modal-header {
    flex-shrink: 0 !important;
  }

  .intake-modal-wrap .ant-modal-body {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch;
  }

  /* Marketing signup: body is a flex shell; inner column scrolls (classNames + data attr fallbacks) */
  .intake-modal-wrap .ant-modal-body.intake-modal-marketing-body,
  .intake-modal-wrap .ant-modal-body:has([data-intake-marketing-layout]) {
    display: flex !important;
    flex-direction: column !important;
    flex: 1 1 auto !important;
    min-height: 0 !important;
    overflow: hidden !important;
    padding: 0 !important;
  }

  /*
   * Terms checkbox: chain modal root + .intake-terms-agree so rules beat Ant in the portal;
   * set font-size on label AND .ant-checkbox + span (Ant often fixes span size).
   * TermsAgreementContainer also duplicates mobile rules with && for injection order safety.
   */
  body .ant-modal-root.clumse-intake-modal .intake-terms-agree .ant-checkbox-wrapper {
    font-family: var(--font-secondary, "Inter", sans-serif) !important;
    font-weight: 400 !important;
    font-size: 0.75rem !important;
    line-height: 1.35 !important;
    color: ${INTAKE_DARK_TEXT} !important;
    display: flex !important;
    align-items: center !important;
    flex-wrap: nowrap !important;
    width: 100% !important;
    max-width: 100% !important;
  }

  body .ant-modal-root.clumse-intake-modal .intake-terms-agree .ant-checkbox-wrapper .ant-checkbox {
    top: 0 !important;
    margin: 0 !important;
    margin-inline-end: 0 !important;
  }

  body .ant-modal-root.clumse-intake-modal .intake-terms-agree .ant-checkbox-wrapper .ant-checkbox + span {
    padding-inline-start: 0 !important;
    margin-inline-start: 0 !important;
    font-size: 0.75rem !important;
    line-height: 1.35 !important;
  }

  body .ant-modal-root.clumse-intake-modal .intake-terms-agree .ant-checkbox-inner {
    border-color: #64748b !important;
    background-color: ${INTAKE_DARK_DEEP} !important;
  }

  body .ant-modal-root.clumse-intake-modal .intake-terms-agree .ant-checkbox-checked .ant-checkbox-inner {
    background-color: var(--color-primary) !important;
    border-color: var(--color-primary) !important;
  }

  @media (max-width: 600px) {
    body .ant-modal-root.clumse-intake-modal .intake-terms-agree .ant-checkbox-wrapper {
      justify-content: center !important;
      text-align: center !important;
      gap: 0.4rem !important;
      font-size: 0.6875rem !important;
      letter-spacing: 0 !important;
      line-height: 1.35 !important;
      width: auto !important;
      max-width: 100% !important;
      margin-inline: auto !important;
    }

    body .ant-modal-root.clumse-intake-modal .intake-terms-agree .ant-checkbox-wrapper .ant-checkbox + span {
      font-size: 0.6875rem !important;
      white-space: nowrap !important;
      flex: 0 1 auto !important;
      min-width: 0 !important;
      text-align: center !important;
      line-height: 1.35 !important;
    }

    body .ant-modal-root.clumse-intake-modal .intake-terms-agree .ant-checkbox-wrapper .ant-checkbox-inner {
      width: 14px !important;
      height: 14px !important;
    }
  }

  @media (max-width: 380px) {
    body .ant-modal-root.clumse-intake-modal .intake-terms-agree .ant-checkbox-wrapper {
      font-size: 0.625rem !important;
      gap: 0.35rem !important;
    }

    body .ant-modal-root.clumse-intake-modal .intake-terms-agree .ant-checkbox-wrapper .ant-checkbox + span {
      font-size: 0.625rem !important;
    }

    body .ant-modal-root.clumse-intake-modal .intake-terms-agree .ant-checkbox-wrapper .ant-checkbox-inner {
      width: 13px !important;
      height: 13px !important;
    }
  }

  @media (min-width: 601px) {
    body .ant-modal-root.clumse-intake-modal .intake-terms-agree .ant-checkbox-wrapper {
      justify-content: center !important;
      gap: 0.5rem !important;
      font-size: 0.75rem !important;
    }

    body .ant-modal-root.clumse-intake-modal .intake-terms-agree .ant-checkbox-wrapper .ant-checkbox + span {
      padding-inline-start: 0.15rem !important;
      white-space: nowrap !important;
      font-size: 0.75rem !important;
    }

    body .ant-modal-root.clumse-intake-modal .intake-terms-agree .ant-checkbox-wrapper .ant-checkbox-inner {
      width: 14px !important;
      height: 14px !important;
    }
  }

  body .ant-modal-root.clumse-intake-modal .intake-terms-agree .ant-checkbox-wrapper a {
    color: #fb923c !important;
    text-decoration: none !important;
    font-weight: 600 !important;
    font-size: 0.75rem !important;
    white-space: nowrap !important;
  }

  body .ant-modal-root.clumse-intake-modal .intake-terms-agree .ant-checkbox-wrapper a:hover {
    color: #fdba74 !important;
    text-decoration: underline !important;
  }
`;

/** Split Ant close out of the scroll column so the X stays pinned (marketing intake only). */
function marketingIntakeModalRender(modalContent) {
  if (!React.isValidElement(modalContent)) return modalContent;
  const kids = React.Children.toArray(modalContent.props.children);
  const closeIdx = kids.findIndex(
    (child) =>
      React.isValidElement(child) &&
      typeof child.props?.className === "string" &&
      /\bant-modal-close\b/.test(child.props.className)
  );
  if (closeIdx < 0) return modalContent;
  const closeNode = kids[closeIdx];
  const other = kids.filter((_, i) => i !== closeIdx);
  const prevStyle =
    modalContent.props.style && typeof modalContent.props.style === "object"
      ? modalContent.props.style
      : {};
  return React.cloneElement(modalContent, {
    ...modalContent.props,
    style: {
      ...prevStyle,
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
      overflow: "hidden",
    },
    children: (
      <>
        <div
          key="intake-close-rail"
          className="intake-mkt-close-rail"
          style={{
            flexShrink: 0,
            position: "relative",
            height: 0,
            width: "100%",
            zIndex: 120,
          }}
        >
          {closeNode}
        </div>
        <div
          key="intake-mkt-scroll-shell"
          className="intake-mkt-scroll-shell"
          style={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {other}
        </div>
      </>
    ),
  });
}

const IntakeModal = ({
  isOpen,
  onClose,
  selectedPlan: initialSelectedPlan,
  onSubmit,
  /** `monthly` | `annual` (must match the pricing page toggle). */
  checkoutBillingCycle = "monthly",
  /** Merged into Stripe checkout request metadata (e.g. pricingTierGroup, source). */
  extraCheckoutMetadata = {},
  /** @deprecated Ignored; unified pricing ladder only. Kept for backward compatibility with older callers. */
  intakeMode: _deprecatedIntakeMode = "pm",
  /** Home hero intake: problem description carried into signup. */
  heroIntakeProblem = "",
  /**
   * Marketing / self-serve surfaces (home hero, pricing, `/?signup`, etc.): light card layout,
   * plan tabs, Clumse hero (no green gradient header). Free skips the portfolio diagnostic gate.
   */
  marketingSignup = false,
  /**
   * `saas`: subscription ladder (PricingSection, etc.).
   * `procurement`: home vendor-job / savings-share plans (Retail … Institutional).
   */
  pricingProduct = "saas",
}) => {
  const isProcurementProduct = pricingProduct === "procurement";
  const isTrialIntake =
    typeof initialSelectedPlan === "string" &&
    initialSelectedPlan.trim().toLowerCase() === "trial";

  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get("invite");
  const navigate = useNavigate();
  const { user, userInfo } = useContext(AuthContext);

  /** Same source as home `PricingSection`: Firestore catalog + optional checkout metadata override. */
  const pricingScheduleGroup = useMemo(() => {
    const fromMeta = extraCheckoutMetadata?.pricingTierGroup;
    if (fromMeta === "legacy_2025" || fromMeta === "market_2026") {
      return fromMeta;
    }
    return (
      userInfo?.pricingTierGroup || user?.pricingTierGroup || "market_2026"
    );
  }, [
    extraCheckoutMetadata?.pricingTierGroup,
    userInfo?.pricingTierGroup,
    user?.pricingTierGroup,
  ]);

  const intakePricingSchedule = useMemo(
    () => getPricingScheduleWithFree(pricingScheduleGroup),
    [pricingScheduleGroup]
  );

  const planOptions = useMemo(() => {
    if (isProcurementProduct) {
      return getProcurementIntakePlanOptions().map((opt) => ({
        ...opt,
        label: marketingSignup ? opt.marketingLabel : opt.label,
      }));
    }
    const base = buildIntakePlanOptions(
      intakePricingSchedule,
      pricingScheduleGroup
    );
    if (marketingSignup) {
      return base.map((opt) => ({
        ...opt,
        label: opt.marketingLabel,
      }));
    }
    return base.map((opt) => ({
      ...opt,
      label: `${planBrandPrefix()} ${opt.label}`,
    }));
  }, [
    isProcurementProduct,
    intakePricingSchedule,
    pricingScheduleGroup,
    marketingSignup,
  ]);

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(() =>
    normalizeInitialPlanForProduct(initialSelectedPlan, pricingProduct)
  );
  const [formValid, setFormValid] = useState(false);
  const billingInterval = "monthly"; // Fixed to monthly only - no annual plans
  const [
    portfolioDiagnosticSatisfied,
    setPortfolioDiagnosticSatisfied,
  ] = useState(true);

  const hasPaidPlanOptions = useMemo(
    () => !isProcurementProduct && planOptions.some((o) => o.price > 0),
    [isProcurementProduct, planOptions]
  );

  const showPlanSelection = planOptions.length > 1;

  const formatPlanOptionPrice = useCallback(
    (opt) => {
      if (isProcurementProduct) {
        return <>{opt.tabPrice || ""}</>;
      }
      if (opt.price === 0) {
        return <>FREE • Up to {opt.units} doors</>;
      }
      if (billingInterval === "annual") {
        const annualUsd = getAnnualDisplayUsd(opt.value, pricingScheduleGroup);
        if (annualUsd != null) {
          return (
            <>
              ${formatAnnualDisplayUsd(annualUsd)}/year • 2 months free • Up to{" "}
              {opt.units} doors
            </>
          );
        }
      }
      return (
        <>
          ${formatMonthlyUsd(opt.price)}/month • Up to {opt.units} doors
        </>
      );
    },
    [isProcurementProduct, billingInterval, pricingScheduleGroup]
  );

  const selectedTier = useMemo(() => {
    if (isProcurementProduct) {
      const d = PROCUREMENT_TIER_DETAIL[selectedPlan];
      if (d) {
        return {
          name: d.name,
          features: [...d.features],
          tagline: d.tagline,
        };
      }
      const r =
        PROCUREMENT_TIER_DETAIL.individual ||
        Object.values(PROCUREMENT_TIER_DETAIL)[0];
      return {
        name: r.name,
        features: [...r.features],
        tagline: r.tagline,
      };
    }
    const tier = intakePricingSchedule[selectedPlan];
    if (tier) return tier;
    const fallbackKey = planOptions[0]?.value;
    return fallbackKey ? intakePricingSchedule[fallbackKey] : null;
  }, [isProcurementProduct, intakePricingSchedule, selectedPlan, planOptions]);

  const planIncludesLines = useMemo(() => {
    const raw = selectedTier?.features;
    if (Array.isArray(raw) && raw.length > 0) return raw;
    const tag = selectedTier?.tagline;
    return tag ? [tag] : [];
  }, [selectedTier]);

  const planIncludesTitleName =
    selectedTier?.name ||
    planOptions.find((p) => p.value === selectedPlan)?.label ||
    selectedPlan;

  const planIncludesDoorLine = useMemo(() => {
    const first = planIncludesLines[0];
    if (!first || typeof first !== "string") return null;
    const t = first.trim().toLowerCase();
    return t.startsWith("up to ") && t.includes("door") ? first.trim() : null;
  }, [planIncludesLines]);

  const planIncludesFeatureLines = useMemo(() => {
    if (planIncludesDoorLine) return planIncludesLines.slice(1);
    return planIncludesLines;
  }, [planIncludesLines, planIncludesDoorLine]);

  useEffect(() => {
    if (isOpen) {
      form.resetFields();
      setSelectedPlan(
        normalizeInitialPlanForProduct(initialSelectedPlan, pricingProduct)
      );
      setFormValid(false);
      // billingInterval is now fixed to "monthly" - no longer a state variable
      // Ensure hidden fields still have expected shape
      form.setFieldsValue({ serviceRequirements: "", chatbotQuestions: "" });
      trackEvent("Intake Modal Viewed", {
        plan: initialSelectedPlan,
        modal_type: "unified_intake_form",
        pricing_product: pricingProduct,
      });
      const initialNorm = normalizeInitialPlanForProduct(
        initialSelectedPlan,
        pricingProduct
      );
      const hasHeroProblem = Boolean(String(heroIntakeProblem || "").trim());
      const isFreeLike =
        initialNorm === "free" ||
        (isProcurementProduct &&
          (initialNorm === "individual" || initialNorm === "retail"));
      setPortfolioDiagnosticSatisfied(
        !isFreeLike || hasHeroProblem || marketingSignup
      );
    }
  }, [
    isOpen,
    form,
    initialSelectedPlan,
    checkoutBillingCycle,
    heroIntakeProblem,
    marketingSignup,
    pricingProduct,
    isProcurementProduct,
  ]);

  // Check form validation on field changes
  const checkFormValidity = () => {
    const values = form.getFieldsValue();
    setFormValid(Boolean(values.name && values.email && values.agreeToTerms));
  };

  const handlePlanChange = (val) => {
    setSelectedPlan(val);
    const isFreeLike =
      val === "free" ||
      (isProcurementProduct && (val === "individual" || val === "retail"));
    if (isFreeLike && !marketingSignup) {
      setPortfolioDiagnosticSatisfied(false);
    } else {
      setPortfolioDiagnosticSatisfied(true);
    }
    setTimeout(() => checkFormValidity(), 0);
  };

  const normalizeWebsiteUrl = (value) => {
    if (!value) return value;
    // Remove any existing protocol
    return value.replace(/^https?:\/\//, "");
  };

  const handleSubmit = async (values) => {
    if (isTrialIntake) {
      setLoading(true);
      try {
        if (typeof onSubmit === "function") await onSubmit(values);
      } catch (err) {
        console.error("[IntakeModal] Trial submit error:", err);
      } finally {
        setLoading(false);
      }
      return;
    }

    if (
      selectedPlan === "free" ||
      (isProcurementProduct &&
        (selectedPlan === "individual" || selectedPlan === "retail"))
    ) {
      setLoading(true);
      try {
        const parts = String(values.name || "")
          .trim()
          .split(/\s+/);
        await submitFreeGatewaySignup({
          email: values.email,
          firstName: parts[0] || "",
          lastName: parts.length > 1 ? parts.slice(1).join(" ") : "",
          phone: "",
          portfolioSize: "",
          propertyType: "other",
          experience: "0-1",
          pricingTierGroup: pricingScheduleGroup,
          inviteCode: inviteCode || undefined,
          agreeToTerms: values.agreeToTerms === true,
        });
        trackEvent("Intake Form Submitted", {
          plan: isProcurementProduct ? selectedPlan : "free",
          has_custom_questions: false,
          has_service_requirements: false,
        });
        message.success(
          "Account created. Check your email for your temporary password, then sign in."
        );
        if (typeof onClose === "function") onClose();
      } catch (err) {
        console.error("[IntakeModal] Free intake error:", err);
        const errMsg =
          err?.message ||
          "Could not create your account. Try again or use a different email.";
        if (errMsg === FREE_GATEWAY_EMAIL_IN_USE) {
          form.setFields([
            {
              name: "email",
              errors: [FREE_GATEWAY_EMAIL_IN_USE],
            },
          ]);
        }
        message.error(errMsg);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Validation for plan and billing cycle
    const validPlans = planOptions.map((p) => p.value);
    if (!validPlans.includes(selectedPlan)) {
      alert("Please select a valid plan.");
      setLoading(false);
      return;
    }

    const resolvedBillingCycle =
      billingInterval === "annual" ? "annual" : "monthly";

    trackEvent("Intake Form Submitted", {
      plan: selectedPlan,
      has_custom_questions: false,
      has_service_requirements: false,
      billing_cycle: resolvedBillingCycle,
    });

    setLoading(true);
    try {
      const propertyTypes = [];

      // Custom intake questions disabled for now
      const chatbotQuestions = [];

      // All plans (starter, growth, pro) are paid plans that go to Stripe checkout
      // No trial signup flow - all plans require payment

      // Compute top-level fields required by backend schema
      const derivedClientName =
        (values.clientName || values.fullName || values.name || "")
          .toString()
          .trim() || (values.email ? values.email.split("@")[0] : "");
      // Procurement → procurement_professional | procurement_institutional (see backend STRIPE_PRICES).
      const normalizedSelectedPlan = resolveIntakeCheckoutPlanKey(
        selectedPlan,
        isProcurementProduct
      ).toLowerCase();

      // For paid plans, create the request body and process Stripe checkout
      const requestBody = {
        email: values.email,
        clientName: derivedClientName,
        phone: "",
        portfolioSize: "",
        propertyTypes,
        billingCycle: resolvedBillingCycle,
        selectedPlan: normalizedSelectedPlan,
        priceId: getPriceId(normalizedSelectedPlan),
        metadata: {
          clientKey: values.clientKey || "",
          portfolioSize: "",
          propertyTypes,
          chatbotQuestions,
          billingCycle: resolvedBillingCycle,
          ...(isProcurementProduct ? { pricingProduct: "procurement" } : {}),
        },
      };

      // Call the create-checkout-session endpoint (authenticated)
      const { firebaseAuth } = await import("../helpers/authentication");
      const currentUser = firebaseAuth?.currentUser;
      const idToken = currentUser ? await currentUser.getIdToken() : null;

      // Ensure landlordId is attached to metadata
      requestBody.metadata = {
        ...requestBody.metadata,
        landlordId: currentUser?.uid || requestBody.metadata?.landlordId || "",
        source: "intake_modal",
        referralCode: "",
        ...extraCheckoutMetadata,
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/stripe/create-checkout-session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("API Error Response:", errorData);
        throw new Error(
          errorData.error ||
            `Failed to create checkout session (HTTP ${response.status})`
        );
      }

      const { url } = await response.json();
      console.log("=== Submission Successful ===");
      console.log("Redirecting to:", url);

      trackEvent("Checkout Started", {
        plan: selectedPlan,
        has_custom_questions: false,
        has_service_requirements: false,
        billing_cycle: resolvedBillingCycle,
      });
      discordTrack("checkout_started", {
        plan: selectedPlan,
        billing_cycle: resolvedBillingCycle,
      });

      window.location.href = url;
    } catch (error) {
      console.error("=== Submission Error ===");
      console.error("Error details:", error);

      trackEvent("Intake Form Submission Error", {
        plan: selectedPlan,
        error_message: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  // Map plan to backend-accepted price keys (no extra envs, matches API template)
  // Note: Backend handles actual Stripe price ID lookup via environment variables
  const getPriceId = (plan) => {
    const p = (plan || "").toLowerCase();
    const PRICE_BY_PLAN = {
      starter: "price_basic",
      growth: "price_growth",
      scale: "price_scale",
      pro: "price_pro",
      business: "price_business",
      chat_starter: "price_chat_starter",
      chat_growth: "price_chat_growth",
      chat_scale: "price_chat_scale",
      chat_pro: "price_chat_growth",
      chat_business: "price_chat_growth",
      procurement_professional: "price_procurement_professional",
      procurement_institutional: "price_procurement_institutional",
    };
    return PRICE_BY_PLAN[p] || "price_basic";
  };

  const handleModalClose = () => {
    trackEvent("Intake Modal Closed", {
      plan: selectedPlan,
      modal_type: "unified_intake_form",
      method: "cancel_button",
    });
    onClose();
  };

  const handleDiagnosticComplete = useCallback(() => {
    setPortfolioDiagnosticSatisfied(true);
    trackEvent("Portfolio Diagnostic Gate Cleared", { plan: selectedPlan });
    if (user) {
      navigate(getPostLoginHomePath(userInfo || {}));
      onClose?.();
    }
  }, [user, userInfo, navigate, onClose, selectedPlan]);

  const headerPlanLabel =
    planOptions.find((p) => p.value === selectedPlan)?.label || selectedPlan;

  // Brand always reflects which product the selected tier belongs to
  const headerBrand = isProcurementProduct ? "Clumse" : planBrandPrefix();
  const isFreePlan =
    (!isProcurementProduct && selectedPlan === "free") ||
    (isProcurementProduct &&
      (selectedPlan === "individual" || selectedPlan === "retail"));
  const operatorDispatchIntake = Boolean(
    String(heroIntakeProblem || "").trim()
  );

  const defaultModalTitle = (
    <ModalHeaderInner>
      <ModalEyebrow>Get started with</ModalEyebrow>
      <ModalHeadline>
        <ModalHeadlineBrand>{headerBrand}</ModalHeadlineBrand>
        <ModalHeadlineSep aria-hidden> - </ModalHeadlineSep>
        <ModalHeadlineTier>
          {headerPlanLabel.replace(/^Clumse\s+/i, "")}
        </ModalHeadlineTier>
      </ModalHeadline>
    </ModalHeaderInner>
  );

  const provisionModalTitle = (
    <ModalHeaderInner>
      <ModalEyebrow>PROVISION MY ACCOUNT</ModalEyebrow>
      <ModalHeadline $provisionHeadline>TO UNLOCK YOUR SAVINGS</ModalHeadline>
    </ModalHeaderInner>
  );

  /** All surfaces that pass `marketingSignup` use the light card layout (including hero “provision” copy). */
  const marketingModalUi = Boolean(marketingSignup);
  const ModalShell = marketingModalUi
    ? MarketingSignupStyledModal
    : StyledModal;
  const selectedPlanOption =
    planOptions.find((o) => o.value === selectedPlan) || planOptions[0];
  const summaryPrice = getPlanPriceParts(
    selectedPlanOption,
    billingInterval,
    pricingScheduleGroup,
    isProcurementProduct
  );
  const planMarketingTitle = isProcurementProduct
    ? (() => {
        const d = PROCUREMENT_TIER_DETAIL[selectedPlan];
        if (d?.name && d?.tagline) return `${d.name}. ${d.tagline}`;
        return selectedTier?.name || "Plan";
      })()
    : HOME_PRICING_PLAN_COLUMN_COPY[selectedPlan]?.headline ||
      `Clumse ${selectedTier?.name || "Plan"}`;

  const marketingSubmitLabel = isFreePlan
    ? "Create Account"
    : isProcurementProduct
    ? "Continue"
    : "Continue";

  const intakeModalShellStyles = useMemo(() => {
    const mask = { backgroundColor: "rgba(0, 0, 0, 0.78)" };
    /** Inline beats Ant Design's wrap overflow:auto so the shell never scrolls as one block. */
    const wrapper = { overflow: "hidden" };
    if (!marketingModalUi) {
      return { mask, wrapper };
    }
    const maxH =
      "calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 3.5rem)";
    return {
      mask,
      wrapper,
      content: {
        maxHeight: maxH,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        padding: 0,
      },
      body: {
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        padding: 0,
      },
    };
  }, [marketingModalUi]);

  const marketingIntakeModalWidth = 480;

  return (
    <>
      <IntakeModalWrapGlobal />
      <ModalShell
        wrapClassName={
          marketingModalUi
            ? "intake-modal-wrap intake-modal-wrap--marketing"
            : "intake-modal-wrap"
        }
        rootClassName="clumse-intake-modal"
        modalRender={marketingModalUi ? marketingIntakeModalRender : undefined}
        title={
          marketingModalUi
            ? null
            : operatorDispatchIntake
            ? provisionModalTitle
            : defaultModalTitle
        }
        open={isOpen}
        onCancel={handleModalClose}
        footer={null}
        width={marketingModalUi ? marketingIntakeModalWidth : 778}
        style={{
          maxWidth: marketingModalUi ? marketingIntakeModalWidth : 778,
        }}
        centered
        closable
        classNames={
          marketingModalUi ? { body: "intake-modal-marketing-body" } : undefined
        }
        styles={intakeModalShellStyles}
      >
        {isFreePlan && !portfolioDiagnosticSatisfied && !marketingSignup ? (
          <FormSection>
            <SystemDiagnostic onComplete={handleDiagnosticComplete} />
          </FormSection>
        ) : (
          <IntakeMarketingBodyRoot
            $marketing={marketingModalUi}
            className={
              marketingModalUi ? "intake-marketing-modal-layout" : undefined
            }
            data-intake-marketing-layout={marketingModalUi ? "" : undefined}
          >
            <IntakeMarketingBodyScroll $marketing={marketingModalUi}>
              {marketingModalUi ? (
                <MarketingSignupHero>
                  <Logo
                    linkTo={null}
                    color="white"
                    size="medium"
                    hideIconFrame
                    fullColorMark
                  />
                  {isProcurementProduct ? (
                    <>
                      <MarketingHeroHeadline>
                        Create Your Account
                      </MarketingHeroHeadline>
                      <MarketingHeroSub>
                        Start getting bids today
                      </MarketingHeroSub>
                    </>
                  ) : (
                    <MarketingHeroTagline>
                      Clumse AI establishes a fair market baseline so you and
                      the contractor align expectations upfront.
                    </MarketingHeroTagline>
                  )}
                </MarketingSignupHero>
              ) : null}
              <StyledForm
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                onValuesChange={checkFormValidity}
                onFinishFailed={(errorInfo) => {
                  console.error("Form submission failed:", errorInfo);
                }}
                requiredMark={true}
                className={
                  marketingModalUi ? "intake-form-marketing" : undefined
                }
              >
                {marketingModalUi ? (
                  <MarketingSignupScroll>
                    {operatorDispatchIntake && (
                      <IntakeTranscriptCallout>
                        <IntakeTranscriptLabel>
                          What you told us
                        </IntakeTranscriptLabel>
                        <IntakeTranscriptBody>
                          {heroIntakeProblem}
                        </IntakeTranscriptBody>
                      </IntakeTranscriptCallout>
                    )}

                    <Form.Item
                      name="name"
                      label="Full name"
                      rules={[
                        {
                          required: true,
                          message: "Please enter your full name",
                        },
                        {
                          min: 2,
                          message: "Name must be at least 2 characters",
                        },
                      ]}
                    >
                      <Input size="large" placeholder="Your Name" />
                    </Form.Item>

                    <Form.Item
                      name="email"
                      label="Email"
                      rules={[
                        {
                          required: true,
                          message: "Please enter your email address",
                        },
                        {
                          type: "email",
                          message: "Please enter a valid email address",
                        },
                      ]}
                      help="Receipts, bids, and savings confirmations go here."
                    >
                      <Input size="large" placeholder="you@email.com" />
                    </Form.Item>
                  </MarketingSignupScroll>
                ) : (
                  <FormSection>
                    {operatorDispatchIntake && (
                      <IntakeTranscriptCallout>
                        <IntakeTranscriptLabel>
                          What you told us
                        </IntakeTranscriptLabel>
                        <IntakeTranscriptBody>
                          {heroIntakeProblem}
                        </IntakeTranscriptBody>
                      </IntakeTranscriptCallout>
                    )}
                    {showPlanSelection ? (
                      <Form.Item
                        label={
                          marketingSignup ? "Choose your plan" : "Select Plan"
                        }
                        className={
                          hasPaidPlanOptions
                            ? "intake-select-plan-with-toggle"
                            : undefined
                        }
                      >
                        <PlanBillingRow>
                          {marketingSignup ? (
                            <PlanToggleWrap
                              role="radiogroup"
                              aria-label="Subscription plan"
                            >
                              {planOptions.map((opt) => (
                                <PlanToggleChip
                                  key={opt.value}
                                  type="button"
                                  role="radio"
                                  aria-checked={selectedPlan === opt.value}
                                  $active={selectedPlan === opt.value}
                                  onClick={() => handlePlanChange(opt.value)}
                                >
                                  <PlanToggleName>{opt.label}</PlanToggleName>
                                  <PlanToggleMeta>
                                    {formatPlanOptionPrice(opt)}
                                  </PlanToggleMeta>
                                </PlanToggleChip>
                              ))}
                            </PlanToggleWrap>
                          ) : (
                            <Select
                              className="intake-plan-select"
                              popupClassName="intake-plan-select-dropdown"
                              value={selectedPlan}
                              onChange={handlePlanChange}
                              size="large"
                              style={{ width: "100%" }}
                            >
                              {planOptions.map((opt) => (
                                <Option key={opt.value} value={opt.value}>
                                  <PlanOptionContent>
                                    <PlanOptionName>{opt.label}</PlanOptionName>
                                    <PlanOptionDetails>
                                      {formatPlanOptionPrice(opt)}
                                    </PlanOptionDetails>
                                  </PlanOptionContent>
                                </Option>
                              ))}
                            </Select>
                          )}
                        </PlanBillingRow>
                      </Form.Item>
                    ) : null}
                    {/* Billing interval removed - monthly only */}

                    {showPlanSelection ? (
                      <PlanIncludesCallout
                        aria-live="polite"
                        aria-atomic="true"
                      >
                        <PlanIncludesTitle>
                          Included with {planIncludesTitleName}
                        </PlanIncludesTitle>
                        {planIncludesLines.length > 0 ? (
                          <>
                            {planIncludesDoorLine && (
                              <PlanIncludesCap>
                                {planIncludesDoorLine}
                              </PlanIncludesCap>
                            )}
                            {planIncludesFeatureLines.length > 0 ? (
                              <PlanIncludesList>
                                {planIncludesFeatureLines.map((line) => (
                                  <li key={line}>
                                    <PlanIncludesBullet aria-hidden="true" />
                                    <PlanIncludesItemText>
                                      {line}
                                    </PlanIncludesItemText>
                                  </li>
                                ))}
                              </PlanIncludesList>
                            ) : null}
                          </>
                        ) : (
                          <PlanIncludesEmpty>
                            Feature list unavailable for this plan. See pricing
                            on the website or contact support.
                          </PlanIncludesEmpty>
                        )}
                        <PlanIncludesFooter>
                          {isProcurementProduct
                            ? isFreePlan
                              ? "No card on Retail. Per-job economics match the home pricing grid when you dispatch work through Clumse."
                              : "Professional and Institutional bill in Stripe Checkout. Per-job fees and savings share still apply per the pricing grid."
                            : isFreePlan
                            ? marketingSignup
                              ? "No payment to create your account on Free. Paid tiers bill in Stripe after checkout. If you stay on Free, we only collect payment when you choose to post a real job and collect vendor bids, or if you upgrade later."
                              : "No payment for the Free Gateway. Your account is created when you submit so you can start immediately. If you sign out, use Forgot password on the login page to set a password for signing in again."
                            : "Checkout and subscription billing are completed in Stripe."}
                        </PlanIncludesFooter>
                      </PlanIncludesCallout>
                    ) : null}

                    <Spacer $marginTop="2rem" />

                    <Spacer $marginTop="3rem" />

                    <Form.Item
                      name="name"
                      label="Full Name"
                      rules={[
                        {
                          required: true,
                          message: "Please enter your full name",
                        },
                        {
                          min: 2,
                          message: "Name must be at least 2 characters",
                        },
                      ]}
                    >
                      <Input size="large" placeholder="John Smith" />
                    </Form.Item>

                    <Spacer $marginTop="2rem" />

                    <Form.Item
                      name="email"
                      label="Contact Email"
                      rules={[
                        {
                          required: true,
                          message: "Please enter your email address",
                        },
                        {
                          type: "email",
                          message: "Please enter a valid email address",
                        },
                      ]}
                      help="Implementation instructions, receipts, and dispatch relay data will be sent to this email"
                    >
                      <Input size="large" placeholder="you@email.com" />
                    </Form.Item>

                    {false && (
                      <Form.Item
                        name="serviceRequirements"
                        label="Tenant Criteria & Requirements"
                        help="Describe any specific requirements or criteria for tenants you accept"
                      >
                        <TextArea
                          rows={4}
                          placeholder="E.g., Credit score requirements, income requirements, pet policies, lease terms, background checks, etc."
                        />
                      </Form.Item>
                    )}

                    {false && (
                      <Form.Item
                        name="chatbotQuestions"
                        label="Custom Intake Questions"
                        help={`All standard questions related to property management will be included automatically. This section is only for any unique or custom questions you'd like to ask potential tenants. Industry standards for rental applications will be followed.`}
                      >
                        <TextArea
                          rows={4}
                          placeholder="E.g., Previous rental history, employment verification, references, move-in timeline, etc."
                        />
                      </Form.Item>
                    )}
                  </FormSection>
                )}

                <Spacer $marginTop="1.25rem" />

                {!isFreePlan && (
                  <PaymentNotice>
                    <PaymentNoticeText>
                      <PaymentNoticeSentence>
                        You will continue to Stripe Checkout.
                      </PaymentNoticeSentence>
                      <PaymentNoticeSentence>
                        Price, renewal, and any promotional terms are shown
                        there before you confirm payment.
                      </PaymentNoticeSentence>
                    </PaymentNoticeText>
                  </PaymentNotice>
                )}

                <Spacer $marginTop="2rem" />

                <TermsAgreementContainer className="intake-terms-agree">
                  <Form.Item
                    name="agreeToTerms"
                    valuePropName="checked"
                    rules={[
                      {
                        validator: (_, value) =>
                          value
                            ? Promise.resolve()
                            : Promise.reject(
                                new Error(
                                  "You must agree to the Terms of Use and Privacy Policy"
                                )
                              ),
                      },
                    ]}
                  >
                    <Checkbox>
                      <IntakeTermsLabel>
                        I agree to the{" "}
                        <Link to={APP_TERMS_ROUTE} target="_blank">
                          Terms of Use
                        </Link>{" "}
                        and{" "}
                        <Link to="/privacy" target="_blank">
                          Privacy Policy
                        </Link>
                      </IntakeTermsLabel>
                    </Checkbox>
                  </Form.Item>
                </TermsAgreementContainer>

                {marketingModalUi ? (
                  <MarketingFooter>
                    <ButtonContainer $marketing>
                      <StyledSpace size="middle">
                        <StyledButton
                          size="large"
                          rounded="none"
                          onClick={handleModalClose}
                          variant="text"
                        >
                          Cancel
                        </StyledButton>
                        <MarketingSubmitButton
                          type="submit"
                          size="large"
                          isLoading={loading}
                          disabled={!formValid || loading}
                        >
                          {marketingSubmitLabel}
                        </MarketingSubmitButton>
                      </StyledSpace>
                    </ButtonContainer>
                  </MarketingFooter>
                ) : (
                  <ButtonContainer>
                    <StyledSpace size="middle">
                      <StyledButton
                        size="large"
                        rounded="none"
                        onClick={handleModalClose}
                        variant="text"
                      >
                        Cancel
                      </StyledButton>
                      <SubmitButton
                        type="submit"
                        size="large"
                        isLoading={loading}
                        disabled={!formValid || loading}
                      >
                        {marketingSignup
                          ? marketingSubmitLabel
                          : "PROVISION MY ACCOUNT"}
                      </SubmitButton>
                    </StyledSpace>
                  </ButtonContainer>
                )}
              </StyledForm>
            </IntakeMarketingBodyScroll>
          </IntakeMarketingBodyRoot>
        )}
      </ModalShell>
    </>
  );
};

const StyledModal = styled(Modal)`
  .ant-modal-content {
    position: relative;
    padding: 0;
    background: ${INTAKE_DARK_PANEL} !important;
    border-radius: 20px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.45), 0 8px 32px rgba(0, 0, 0, 0.35);
    border: 1px solid ${INTAKE_DARK_BORDER};
    overflow: hidden;
  }

  @media (max-width: 480px) {
    .ant-modal-content {
      border-radius: 16px;
    }
  }

  .ant-modal-header {
    margin-bottom: 0;
    padding: 1.85rem 3.5rem 1.95rem 2.35rem;
    background: linear-gradient(
      160deg,
      ${INTAKE_DARK_DEEP} 0%,
      ${INTAKE_DARK_PANEL} 38%,
      rgba(44, 95, 45, 0.42) 100%
    );
    border-bottom: 1px solid ${INTAKE_DARK_BORDER};
    border-radius: 20px 20px 0 0;
    position: relative;
    box-shadow: 0 10px 28px -14px rgba(0, 0, 0, 0.45);

    &::after {
      content: "";
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 12px;
      background: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0.2) 0%,
        transparent 100%
      );
      pointer-events: none;
    }
  }

  @media (max-width: 768px) {
    .ant-modal-header {
      padding: 1.5rem 3.25rem 1.65rem 1.75rem;
      border-radius: 20px 20px 0 0;
    }
  }

  @media (max-width: 480px) {
    .ant-modal-header {
      padding: 1.35rem 2.85rem 1.5rem 1.25rem;
      border-radius: 16px 16px 0 0;
    }
  }

  .ant-modal-body {
    padding: 0;
    background: linear-gradient(
      180deg,
      ${INTAKE_DARK_DEEP} 0%,
      ${INTAKE_DARK_PANEL} 28px
    );
  }

  .ant-modal-title {
    background: none !important;
    width: 100%;
    margin: 0 !important;
    padding: 0 !important;
    line-height: normal !important;
  }

  .ant-select-arrow {
    top: 60%;
    color: var(--color-primary) !important;
  }

  .ant-modal-close {
    position: absolute !important;
    display: flex !important;
    align-items: center;
    justify-content: center;
    z-index: 50 !important;
    top: 1.5rem !important;
    right: 1.5rem !important;
    width: 40px;
    height: 40px;
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.28);
    border-radius: 50%;
    font-size: 1.1rem;
    color: white !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    backdrop-filter: blur(10px);
  }

  @media (max-width: 480px) {
    .ant-modal-close {
      top: 1.25rem !important;
      right: 1.25rem !important;
      width: 36px;
      height: 36px;
    }
  }

  .ant-modal-close .anticon {
    color: white !important;
    font-size: 1.25rem;
  }

  .ant-modal-close svg {
    color: white !important;
    fill: white !important;
  }

  .ant-modal-close:hover {
    color: #ffffff !important;
    background: rgba(255, 87, 34, 0.22) !important;
    border-color: rgba(255, 87, 34, 0.55) !important;
    transform: scale(1.05);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
  }

  .ant-modal-close:hover .anticon {
    color: #ffffff !important;
  }

  .ant-modal-close:hover svg {
    color: #ffffff !important;
    fill: #ffffff !important;
  }
`;

/** Global signup: dark industrial card, no gradient header. */
const MarketingSignupStyledModal = styled(Modal)`
  .ant-modal-content {
    position: relative !important;
    padding: 0 !important;
    background: ${INTAKE_DARK_PANEL} !important;
    border-radius: 20px;
    box-shadow: 0 24px 72px rgba(0, 0, 0, 0.5), 0 2px 8px rgba(0, 0, 0, 0.35);
    border: 1px solid ${INTAKE_DARK_BORDER};
    overflow: hidden;
  }

  .ant-modal-header {
    display: none !important;
  }

  .ant-modal-body {
    padding: 0 !important;
    background: ${INTAKE_DARK_PANEL} !important;
  }

  .ant-modal-close {
    position: absolute !important;
    top: 1.5rem !important;
    right: 1.5rem !important;
    width: 40px !important;
    height: 40px !important;
    background: rgba(255, 255, 255, 0.08) !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    border-radius: 50% !important;
    color: #f8fafc !important;
    box-shadow: none !important;
    backdrop-filter: none !important;
    z-index: 50 !important;
  }

  .ant-modal-close .anticon,
  .ant-modal-close svg {
    color: #f8fafc !important;
    fill: #f8fafc !important;
  }

  .ant-modal-close:hover {
    background: rgba(255, 87, 34, 0.22) !important;
    border-color: rgba(255, 87, 34, 0.55) !important;
    color: #fff !important;
    transform: none !important;
  }

  .ant-modal-close:hover .anticon,
  .ant-modal-close:hover svg {
    color: #fff !important;
    fill: #fff !important;
  }

  @media (max-width: 480px) {
    .ant-modal-content {
      border-radius: 16px;
    }

    .ant-modal-close {
      top: 1.25rem !important;
      right: 1.25rem !important;
      width: 36px !important;
      height: 36px !important;
    }
  }
`;

const ModalHeaderInner = styled.div`
  max-width: 100%;
  padding-right: 0.25rem;
`;

const ModalEyebrow = styled.span`
  display: block;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.72);
  font-family: var(--font-secondary);
  margin-bottom: 0.4rem;
`;

const modalHeadlineTitleSize = css`
  font-size: clamp(1.1rem, 3.2vw, 1.4rem);
`;

const ModalHeadline = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "$provisionHeadline",
})`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem 0.35rem;
  line-height: 1.12;
  text-shadow: none;

  ${(p) =>
    p.$provisionHeadline
      ? `
    justify-content: center;
    text-align: center;
    width: 100%;
    font-family: var(--font-mono, "IBM Plex Mono", ui-monospace, monospace);
    font-size: clamp(0.85rem, 2.2vw, 1.05rem);
    font-weight: 800;
    letter-spacing: 0.1em;
    color: #ffffff;
  `
      : ""}
`;

const ModalHeadlineBrand = styled.span`
  ${modalHeadlineTitleSize}
  font-weight: 700;
  font-family: var(--font-primary);
  color: #fff;
  letter-spacing: -0.03em;
`;

const ModalHeadlineSep = styled.span`
  ${modalHeadlineTitleSize}
  font-weight: 600;
  font-family: var(--font-primary);
  color: rgba(255, 255, 255, 0.65);
`;

const ModalHeadlineTier = styled.span`
  ${modalHeadlineTitleSize}
  font-weight: 700;
  font-family: var(--font-secondary);
  color: #fff;
  letter-spacing: -0.03em;
`;

const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 2rem;
  }

  /* Tight gap to monthly/annual toggle; default 2rem is too large there */
  .intake-select-plan-with-toggle.ant-form-item {
    margin-bottom: 0.25rem;
  }

  .ant-form-item-label > label {
    color: ${INTAKE_DARK_TEXT} !important;
    font-weight: 600;
    font-size: 0.95rem;
    font-family: var(--font-secondary);
    letter-spacing: -0.01em;
    margin-bottom: 0.75rem;
  }

  .ant-form-item-label {
    color: ${INTAKE_DARK_TEXT} !important;
  }

  .ant-form-item-explain {
    color: rgba(226, 232, 240, 0.88);
    font-size: 0.9rem;
    margin-top: 0.5rem;
  }

  .ant-form-item-help {
    color: ${INTAKE_DARK_MUTED};
    font-size: 0.95rem;
    margin-top: 0.5rem;
    font-family: var(--font-secondary);
  }

  /* Help/tooltips (not errors) - force override */
  .ant-form-item .ant-form-item-help:not(.ant-form-item-explain-error),
  .ant-form-item-explain.ant-form-item-help:not(.ant-form-item-explain-error) {
    color: ${INTAKE_DARK_MUTED} !important;
    font-size: 0.95rem;
    margin-top: 0.5rem;
    font-family: var(--font-secondary);
  }

  /* Error messages */
  .ant-form-item-explain-error {
    color: var(--color-error) !important;
    font-size: 0.95rem;
    margin-top: 0.5rem;
    font-family: var(--font-secondary);
    text-align: center;
  }

  .ant-select-selection-placeholder,
  input::placeholder,
  textarea::placeholder {
    color: var(--color-text-muted);
    font-family: var(--font-secondary);
  }

  .ant-checkbox-wrapper {
    color: ${INTAKE_DARK_TEXT};
    font-family: var(--font-secondary);
    font-weight: 500;
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: var(--color-primary);
    border-color: var(--color-primary);
  }

  .ant-checkbox-inner {
    background-color: ${INTAKE_DARK_DEEP};
    border-color: #64748b;
    border-radius: 6px;
  }

  .ant-select-selector {
    background-color: ${INTAKE_DARK_CARD} !important;
    border: 2px solid ${INTAKE_DARK_BORDER} !important;
    border-radius: 12px !important;
    padding: 0.875rem 1rem !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  }

  .ant-select-selector:hover {
    border-color: var(--color-primary) !important;
    box-shadow: 0 0 0 3px rgba(205, 175, 99, 0.1) !important;
  }

  .ant-select-focused .ant-select-selector {
    border-color: var(--color-primary) !important;
    box-shadow: 0 0 0 3px rgba(205, 175, 99, 0.15) !important;
  }

  .ant-select-selection-item {
    color: ${INTAKE_DARK_TEXT} !important;
    font-family: var(--font-secondary);
    font-weight: 500;
  }

  /* Plan picker: allow multi-line label so price/units never sit under the caret */
  .intake-plan-select.ant-select-single {
    height: auto !important;
    min-height: 40px;
  }

  .intake-plan-select.ant-select-single:not(.ant-select-customize-input)
    .ant-select-selector {
    height: auto !important;
    min-height: 40px !important;
    align-items: center !important;
  }

  .intake-plan-select .ant-select-selection-item {
    display: flex !important;
    align-items: flex-start !important;
    white-space: normal !important;
    overflow: visible !important;
    text-overflow: clip !important;
    line-height: 1.35 !important;
    align-self: center !important;
    padding-inline-end: 2rem !important;
  }

  .intake-plan-select .ant-select-arrow {
    top: 50% !important;
    inset-inline-end: 14px !important;
  }

  .ant-input,
  .ant-input-affix-wrapper,
  .ant-input-status-error,
  .ant-input-status-error .ant-input,
  .ant-input-status-error .ant-input-affix-wrapper,
  .ant-input-affix-wrapper-status-error,
  .ant-input-affix-wrapper-status-error .ant-input,
  .ant-select-selector,
  textarea,
  input {
    background: ${INTAKE_DARK_CARD} !important;
    color: ${INTAKE_DARK_TEXT} !important;
    border: 2px solid ${INTAKE_DARK_BORDER} !important;
    border-radius: 0 !important;
    padding: 0.875rem 1rem !important;
    font-size: 1rem !important;
    font-family: var(--font-secondary) !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  }

  .ant-input:focus,
  .ant-input-affix-wrapper:focus,
  .ant-input-affix-wrapper-focused,
  textarea:focus,
  input:focus {
    border-color: #ff5722 !important;
    box-shadow: 0 0 0 1px #ff5722, 0 0 14px rgba(255, 87, 34, 0.25) !important;
    outline: none !important;
  }

  /* All custom styles for .ant-input-group-addon and related selectors are removed. Use Ant Design defaults for the website input field. */

  .ant-form-item-label > label[for="practiceAreas"] {
    font-size: 0.95rem !important;
    font-weight: 600 !important;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Fix dropdown text color */
  .ant-select-dropdown {
    background: ${INTAKE_DARK_CARD} !important;
    border: 1px solid ${INTAKE_DARK_BORDER} !important;
    border-radius: 12px !important;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45) !important;
  }

  .ant-select-item {
    color: ${INTAKE_DARK_TEXT} !important;
    background: ${INTAKE_DARK_CARD} !important;
    font-family: var(--font-secondary) !important;
  }

  .ant-select-item-option {
    color: ${INTAKE_DARK_TEXT} !important;
    background: ${INTAKE_DARK_CARD} !important;
    padding: 0.75rem 1rem !important;
  }

  .ant-select-item-option-content {
    color: ${INTAKE_DARK_TEXT} !important;
    font-weight: 500;
  }

  .intake-plan-select-dropdown .ant-select-item-option-content {
    display: flex !important;
    align-items: flex-start !important;
    min-height: 0;
  }

  .ant-select-item-option-active {
    background-color: rgba(255, 87, 34, 0.15) !important;
    color: #ffffff !important;
  }

  .ant-select-item-option-selected {
    background-color: rgba(44, 95, 45, 0.35) !important;
    color: #e2e8f0 !important;
    font-weight: 600;
  }

  &.intake-form-marketing .ant-form-item {
    margin-bottom: 1.35rem;
  }

  /* Ant ConfigProvider light theme sets dark label colors; force readable light text */
  &.intake-form-marketing .ant-form-item-label,
  &.intake-form-marketing .ant-form-item-label > label {
    color: ${INTAKE_MARKETING_LABEL} !important;
  }

  &.intake-form-marketing .ant-form-item-label > label {
    font-size: 0.6875rem;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  &.intake-form-marketing .intake-marketing-plan-heading .ant-form-item-label {
    text-align: center;
  }

  &.intake-form-marketing
    .intake-marketing-plan-heading
    .ant-form-item-label
    > label {
    width: 100%;
    display: inline-flex;
    justify-content: center;
  }

  &.intake-form-marketing
    .ant-form-item-label
    > label.ant-form-item-required:not(.ant-form-item-required-mark-optional)::before {
    color: #fb923c !important;
  }

  &.intake-form-marketing .ant-form-item-help {
    color: ${INTAKE_DARK_MUTED} !important;
    font-size: 0.8125rem;
  }

  &.intake-form-marketing .intake-terms-agree .ant-checkbox-wrapper,
  &.intake-form-marketing
    .intake-terms-agree
    .ant-checkbox-wrapper
    .ant-checkbox
    + span {
    font-size: 0.75rem !important;
    font-weight: 400 !important;
    line-height: 1.35 !important;
  }

  &.intake-form-marketing .intake-terms-agree .ant-checkbox-inner {
    width: 14px !important;
    height: 14px !important;
  }

  &.intake-form-marketing .ant-input {
    border-radius: 12px !important;
    border: 1px solid ${INTAKE_DARK_BORDER} !important;
  }

  &.intake-form-marketing .ant-input:focus {
    border-color: #ff5722 !important;
    box-shadow: 0 0 0 3px rgba(255, 87, 34, 0.18) !important;
  }
`;

const PlanOptionContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 0.25rem;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

const PlanOptionName = styled.span`
  font-weight: 600;
  font-size: 1rem;
  color: ${INTAKE_DARK_TEXT};
  line-height: 1.35;
  width: 100%;
`;

const PlanOptionDetails = styled.span`
  font-size: 0.875rem;
  color: ${INTAKE_DARK_MUTED};
  font-weight: 400;
  width: 100%;
  min-width: 0;
  white-space: normal;
  overflow-wrap: break-word;
  word-break: break-word;
  line-height: 1.35;
  text-align: left;
`;

/** Marketing signup: shell (absolute close) + inner column scroll */
const IntakeMarketingBodyRoot = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "$marketing",
})`
  ${(p) =>
    p.$marketing
      ? css`
          position: relative;
          flex: 1 1 auto;
          min-height: 0;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          width: 100%;
          box-sizing: border-box;
        `
      : css`
          display: contents;
        `}
`;

const IntakeMarketingBodyScroll = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "$marketing",
})`
  ${(p) =>
    p.$marketing
      ? css`
          flex: 1 1 auto;
          min-height: 0;
          overflow-x: hidden;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          width: 100%;
          box-sizing: border-box;
        `
      : css`
          display: contents;
        `}
`;

const MarketingSignupHero = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  /* Clear the modal’s absolute close (top ~1.5rem + 40px); no in-body sticky bar. */
  padding: clamp(2.25rem, 5vw, 2.75rem) 1.75rem 1.25rem;
  border-bottom: 1px solid ${INTAKE_DARK_BORDER};
  background: linear-gradient(
    180deg,
    ${INTAKE_DARK_DEEP} 0%,
    ${INTAKE_DARK_PANEL} 100%
  );
  box-sizing: border-box;

  /* Center the logo lockup (Logo root is a flex row). */
  & > div:first-of-type {
    display: flex;
    justify-content: center;
    width: 100%;
  }

  @media (max-width: 480px) {
    padding: clamp(2.35rem, 6vw, 2.85rem) 1.75rem 1.15rem;
  }
`;

const MarketingHeroHeadline = styled.h2`
  margin: 1rem auto 0;
  max-width: 28rem;
  font-family: var(--font-secondary, "Inter", sans-serif);
  font-size: clamp(1rem, 2.4vw, 1.2rem);
  font-weight: 800;
  line-height: 1.3;
  letter-spacing: -0.02em;
  text-align: center;
  color: ${INTAKE_DARK_TEXT};
`;

const MarketingHeroSub = styled.p`
  margin: 0.55rem auto 0;
  max-width: 28rem;
  font-family: var(--font-secondary, "Inter", sans-serif);
  font-size: 0.85rem;
  font-weight: 500;
  line-height: 1.5;
  text-align: center;
  color: ${INTAKE_DARK_MUTED};
`;

const MarketingHeroTagline = styled.p`
  margin: 0.85rem auto 0;
  max-width: 22rem;
  font-family: var(--font-secondary, "Inter", sans-serif);
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.5;
  color: ${INTAKE_DARK_MUTED};
`;

const MarketingSignupScroll = styled.div`
  padding: 1.25rem 1.5rem 0.25rem;
  box-sizing: border-box;

  @media (max-width: 480px) {
    padding: 1.15rem 1rem 0.15rem;
  }
`;

const MarketingPlanTabRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 0.45rem;
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 0.15rem;
`;

const MarketingPlanTab = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== "$active",
})`
  flex: 1 1 0;
  min-width: 5.5rem;
  text-align: center;
  padding: 0.55rem 0.45rem;
  border-radius: 10px;
  border: 2px solid
    ${(p) => (p.$active ? "rgba(255, 87, 34, 0.65)" : INTAKE_DARK_BORDER)};
  background: ${(p) => (p.$active ? INTAKE_DARK_CARD : INTAKE_DARK_DEEP)};
  color: ${(p) => (p.$active ? "#ffffff" : INTAKE_DARK_TEXT)};
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease,
    box-shadow 0.15s ease;
  box-shadow: ${(p) => (p.$active ? "0 4px 14px rgba(0, 0, 0, 0.35)" : "none")};

  &:focus-visible {
    outline: 2px solid #ff5722;
    outline-offset: 2px;
  }
`;

const MarketingPlanTabName = styled.div`
  font-family: var(--font-secondary, "Inter", sans-serif);
  font-weight: 800;
  font-size: 0.8125rem;
  letter-spacing: 0.02em;
  line-height: 1.25;
`;

const MarketingPlanTabMeta = styled.div`
  margin-top: 0.15rem;
  font-family: var(--font-secondary, "Inter", sans-serif);
  font-size: 0.68rem;
  font-weight: 600;
  line-height: 1.2;
  opacity: 0.88;
  color: inherit;
`;

const MarketingPlanCard = styled.div`
  margin-top: 1rem;
  padding: 1.1rem 1rem 0.85rem;
  border: 1px solid ${INTAKE_DARK_BORDER};
  border-radius: 14px;
  background: ${INTAKE_DARK_CARD};
  box-sizing: border-box;
`;

const MarketingPlanCardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.65rem;
`;

const MarketingPlanCardTitle = styled.h3`
  margin: 0;
  font-family: var(--font-secondary, "Inter", sans-serif);
  font-size: 1.05rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: ${INTAKE_DARK_TEXT};
`;

const MarketingPlanCardPrice = styled.div`
  margin-top: 0.2rem;
  display: flex;
  align-items: baseline;
  gap: 0.2rem;
  font-family: var(--font-mono, "IBM Plex Mono", monospace);
  font-variant-numeric: tabular-nums;
`;

const MarketingPlanCardPriceMain = styled.span`
  font-size: 1.45rem;
  font-weight: 500;
  color: ${INTAKE_DARK_TEXT};
  letter-spacing: -0.03em;
`;

const MarketingPlanCardPriceSuffix = styled.span`
  font-size: 0.9rem;
  font-weight: 500;
  color: ${INTAKE_DARK_MUTED};
`;

const MarketingFeatureList = styled.ul`
  list-style: none;
  margin: 0.5rem 0 0;
  padding: 0;
  width: 100%;
  max-width: none;
  box-sizing: border-box;

  li {
    display: flex;
    align-items: flex-start;
    gap: 0.65rem;
    padding: 0.55rem 0;
    font-family: var(--font-secondary, "Inter", sans-serif);
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.5;
    color: #a1a1aa;
    border-bottom: 1px solid ${INTAKE_DARK_BORDER};

    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }
  }
`;

const MarketingFeatureText = styled.div`
  flex: 1;
  min-width: 0;
  text-wrap: pretty;
`;

const MarketingFeatureLead = styled.div`
  font-weight: 700;
  color: #e2e8f0;
  line-height: 1.35;
  margin-bottom: 0.2rem;
`;

const MarketingFeatureDetail = styled.div`
  font-weight: 500;
  color: #cbd5e1;
  line-height: 1.55;
`;

const AdvisorPlanGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-top: 0.5rem;
  width: 100%;
  align-items: stretch;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const AdvisorPlanCard = styled.button`
  background: ${(props) =>
    props.$selected ? "rgba(255, 87, 34, 0.1)" : INTAKE_DARK_CARD};
  border: 2px solid
    ${(props) => (props.$selected ? "#ff5722" : INTAKE_DARK_BORDER)};
  border-radius: 12px;
  padding: 2rem 1.25rem 1.25rem;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  width: 100%;
  min-width: 0;
  min-height: 360px;
  max-height: 360px;
  box-sizing: border-box;
  display: grid;
  grid-template-rows: minmax(2.75rem, auto) auto minmax(0, 1fr) auto;
  grid-template-columns: 1fr;
  align-items: start;
  row-gap: 0.55rem;

  &:hover {
    border-color: #ff5722;
    background: rgba(255, 87, 34, 0.05);
  }

  &:focus {
    outline: none;
    border-color: #ff5722;
    box-shadow: 0 0 0 3px rgba(255, 87, 34, 0.2);
  }

  /* Stacked cards: drop fixed height + 1fr features row so footer sits under bullets */
  @media (max-width: 767px) {
    min-height: 0;
    max-height: none;
    grid-template-rows: minmax(2.75rem, auto) auto auto auto;
    padding: 1.65rem 1.15rem 1.05rem;
  }
`;

const AdvisorPlanTitle = styled.h3`
  font-size: 1.05rem;
  font-weight: 700;
  color: ${INTAKE_DARK_TEXT};
  margin: 0;
  line-height: 1.2;
  width: 100%;
  min-height: calc(1.05rem * 1.2 * 2);
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  display: flex;
  align-items: center;
  justify-content: center;
  hyphens: none;
`;

const AdvisorPlanPrice = styled.div`
  font-family: var(--font-mono, "IBM Plex Mono", monospace);
  font-size: 2.5rem;
  font-weight: 500;
  color: #ff5722;
  line-height: 1;
  width: 100%;
  text-align: center;
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.25rem;
  font-variant-numeric: tabular-nums;
  margin: 0;
`;

const AdvisorPlanPeriod = styled.span`
  font-family: var(--font-mono, "IBM Plex Mono", monospace);
  font-size: 1rem;
  font-weight: 500;
  color: ${INTAKE_DARK_MUTED};
`;

const AdvisorPlanCTA = styled.div`
  background: #ff5722;
  color: white;
  font-size: 0.9rem;
  font-weight: 700;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  text-align: center;
  margin: 1rem 0 0.5rem 0;
  transition: background 0.2s ease;

  &:hover {
    background: #f4511e;
  }
`;

const AdvisorPlanFeeSchedule = styled.div`
  font-family: var(--font-mono, "IBM Plex Mono", monospace);
  font-size: 0.7rem;
  color: #a1a1aa;
  text-align: center;
  margin: 0;
  padding-top: 0.35rem;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  align-self: stretch;
`;

const AdvisorPlanFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  min-height: 0;
  width: 100%;
  display: grid;
  grid-template-rows: repeat(3, minmax(2.85rem, auto));
  gap: 0.45rem;
  align-content: start;

  li {
    font-size: 0.85rem;
    color: #a1a1aa;
    margin: 0;
    padding-left: 1.2rem;
    position: relative;
    line-height: 1.4;
    font-weight: 500;
    display: flex;
    align-items: flex-start;
    min-height: 2.85rem;

    &::before {
      content: "✓";
      position: absolute;
      left: 0;
      top: 0.12rem;
      color: #4ade80;
      font-weight: 700;
      font-size: 0.9rem;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  /* Three-column desktop: space under price, compact bullet stack */
  @media (min-width: 768px) {
    margin-top: 0.75rem;
    grid-template-rows: repeat(3, auto);
    gap: 0.12rem;

    li {
      min-height: 0;
      padding-top: 0.15rem;
      padding-bottom: 0.15rem;
    }
  }

  /* Stacked plan cards: breathe under price, tighter between bullets */
  @media (max-width: 767px) {
    margin-top: 0.7rem;
    grid-template-rows: repeat(3, auto);
    gap: 0.18rem;

    li {
      min-height: 0;
      padding-top: 0.1rem;
      padding-bottom: 0.1rem;
    }
  }
`;

const AdvisorSelectedBadge = styled.div`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: #ff5722;
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  box-shadow: 0 2px 8px rgba(255, 87, 34, 0.3);
  white-space: nowrap;
  z-index: 2;
`;

const AdvisorPlanBidsGuaranteeNote = styled.p`
  margin: 1rem 0 0;
  padding: 0;
  width: 100%;
  text-align: center;
  font-size: 0.875rem;
  font-weight: 600;
  color: #4ade80;
  line-height: 1.45;
  letter-spacing: 0.02em;
`;

const MarketingFeatureCheck = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.08rem;
  color: #4ade80;
  font-size: 0.75rem;
  line-height: 1;
`;

const MarketingPlanCardFoot = styled.p`
  margin: 0.75rem 0 0;
  padding-top: 0.65rem;
  border-top: 1px solid ${INTAKE_DARK_BORDER};
  font-size: 0.6875rem;
  font-family: var(--font-secondary, "Inter", sans-serif);
  font-weight: 500;
  line-height: 1.45;
  color: ${INTAKE_DARK_MUTED};
`;

const MarketingSectionRule = styled.div`
  height: 1px;
  margin: 1.35rem 0 1rem;
  background: linear-gradient(
    90deg,
    transparent,
    ${INTAKE_DARK_BORDER} 12%,
    ${INTAKE_DARK_BORDER} 88%,
    transparent
  );
`;

const MarketingFormLead = styled.p`
  margin: 0 0 1.1rem;
  text-align: center;
  font-family: var(--font-secondary, "Inter", sans-serif);
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.5;
  color: ${INTAKE_DARK_MUTED};
`;

const MarketingFooter = styled.div`
  /* Same as modal body: avoid a second, darker strip behind the buttons */
  background: ${INTAKE_DARK_PANEL};
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
`;

const MarketingFooterText = styled.p`
  margin: 1rem auto 1rem;
  max-width: 28rem;
  text-align: center;
  font-family: "Inter", system-ui, sans-serif;
  font-size: 0.8rem;
  color: rgba(148, 163, 184, 0.75);
  line-height: 1.45;
  word-wrap: break-word;
  hyphens: auto;
`;

const FormSection = styled.div`
  margin: 0 auto;
  padding: 2.5rem;
  padding-top: 1.75rem;
  padding-bottom: 0.5rem;
  max-width: 36rem;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    padding-top: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1.5rem 1rem;
    padding-top: 1.35rem;
  }
`;

const IntakeTranscriptCallout = styled.div`
  margin: 0 0 1.25rem;
  padding: 0.75rem 0.85rem;
  border: 2px solid ${INTAKE_DARK_BORDER};
  border-radius: 0;
  background: rgba(30, 41, 59, 0.55);
  box-sizing: border-box;
`;

const IntakeTranscriptLabel = styled.div`
  font-family: var(--font-mono, "IBM Plex Mono", ui-monospace, monospace);
  font-size: 0.55rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${INTAKE_DARK_MUTED};
  margin-bottom: 0.45rem;
`;

const IntakeTranscriptBody = styled.div`
  font-family: var(--font-mono, "IBM Plex Mono", ui-monospace, monospace);
  font-size: 0.78rem;
  line-height: 1.5;
  color: ${INTAKE_DARK_TEXT};
  white-space: pre-wrap;
`;

const PlanIncludesCallout = styled.div`
  padding: 0.75rem 0.9rem;
  margin: 0.35rem 0 1.1rem;
  background: ${INTAKE_DARK_CARD};
  border: 1px solid ${INTAKE_DARK_BORDER};
  border-radius: 10px;
  color: ${INTAKE_DARK_TEXT};
  box-shadow: none;
`;

const PlanIncludesTitle = styled.p`
  margin: 0 0 0.45rem 0;
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: var(--font-secondary);
  color: ${INTAKE_DARK_MUTED};
  line-height: 1.3;
  letter-spacing: 0.01em;
`;

const PlanIncludesCap = styled.div`
  display: inline-block;
  margin: 0 0 0.45rem 0;
  padding: 0.2rem 0.5rem;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-family: var(--font-secondary);
  color: var(--color-primary-darker);
  background: rgba(44, 95, 45, 0.09);
  border-radius: 6px;
  line-height: 1.25;
`;

const PlanIncludesList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  font-size: 0.5625rem;
  font-family: var(--font-secondary);
  font-weight: 500;
  line-height: 1.45;
  color: ${INTAKE_DARK_MUTED};

  li {
    display: flex;
    align-items: flex-start;
    gap: 0.35rem;
    margin: 0;
    padding: 0.28rem 0;
    border-bottom: 1px solid rgba(148, 163, 184, 0.15);

    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }

    &:first-child {
      padding-top: 0;
    }
  }
`;

/** Dot centered on first line: half the line box (1.45) minus half the 3px mark. */
const PlanIncludesBullet = styled.span`
  width: 3px;
  height: 3px;
  flex-shrink: 0;
  border-radius: 50%;
  background: var(--color-primary);
  opacity: 0.85;
  margin-top: calc(0.725em - 1.5px);
`;

const PlanIncludesItemText = styled.span`
  flex: 1;
  min-width: 0;
`;

const PlanIncludesEmpty = styled.p`
  margin: 0 0 0.5rem 0;
  font-size: 0.75rem;
  font-family: var(--font-secondary);
  color: ${INTAKE_DARK_MUTED};
  line-height: 1.45;
`;

const PlanIncludesFooter = styled.p`
  margin: 0.55rem 0 0 0;
  padding-top: 0.5rem;
  border-top: 1px solid ${INTAKE_DARK_BORDER};
  font-size: 0.6875rem;
  font-family: var(--font-secondary);
  color: ${INTAKE_DARK_MUTED};
  line-height: 1.4;
`;

const DisclaimerText = styled.p`
  font-size: 0.875rem;
  color: ${INTAKE_DARK_MUTED};
  margin: 1.5rem 10%;
  text-align: center;
  font-family: var(--font-secondary);
  line-height: 1.5;

  a {
    color: #fb923c;
    text-decoration: none;
    font-weight: 600;
    transition: color 0.3s ease;

    &:hover {
      color: #fdba74;
      text-decoration: underline;
    }
  }
`;

const PaymentNotice = styled.div`
  display: block;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  margin: 2rem 0;
  margin-bottom: 0;
  padding: 0.875rem 1.5rem;
  background: linear-gradient(
    135deg,
    rgba(44, 95, 45, 0.1) 0%,
    rgba(44, 95, 45, 0.05) 100%
  );
  border: 1px solid var(--color-primary-light);
  border-left: 0px;
  border-right: 0px;
  position: relative;
`;

const PaymentNoticeText = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 40rem;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.15rem;
  text-align: center;
`;

const PaymentNoticeSentence = styled.p`
  margin: 0;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.35;
  font-family: var(--font-secondary);
  color: ${INTAKE_DARK_TEXT};
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
`;

const ButtonContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "$marketing",
})`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  margin-top: ${(p) => (p.$marketing ? "0.35rem" : "2rem")};
  padding: ${(p) =>
    p.$marketing ? "0 1.75rem 1.75rem" : "0 2.5rem 2.5rem 2.5rem"};
  gap: 1.5rem;

  ${(p) =>
    p.$marketing
      ? `
    flex-direction: column-reverse;
    align-items: stretch;
    max-width: 28rem;
    margin-left: auto;
    margin-right: auto;
    & button[type="submit"] {
      width: 100%;
      max-width: 100%;
    }
  `
      : ""}
`;

const StyledSpace = styled(Space)`
  gap: 1.5rem !important;
  width: 100%;
  display: flex !important;
  justify-content: center !important;
  flex-wrap: wrap;
`;

const StyledButton = styled(Button)`
  ${(p) =>
    p.variant === "text"
      ? css`
          background: transparent !important;
          border: none !important;
          box-shadow: none !important;
          color: ${INTAKE_DARK_MUTED} !important;
          font-weight: 500 !important;
          font-family: var(--font-secondary) !important;
          font-size: 0.9rem !important;
          padding: 0.25rem 0.5rem !important;
          min-width: auto !important;
          height: auto !important;
          border-radius: 0 !important;

          &:hover {
            background: transparent !important;
            border: none !important;
            color: ${INTAKE_DARK_TEXT} !important;
            transform: none !important;
            box-shadow: none !important;
          }

          &:focus-visible {
            outline: 2px solid ${INTAKE_DARK_BORDER};
            outline-offset: 2px;
          }
        `
      : css`
          background-color: ${INTAKE_DARK_CARD};
          color: ${INTAKE_DARK_TEXT};
          border: 2px solid ${INTAKE_DARK_BORDER};
          border-radius: 12px;
          padding: 0.875rem 2rem;
          font-size: 1rem;
          font-weight: 600;
          font-family: var(--font-secondary);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          min-width: 120px;

          &:hover {
            background-color: #334155;
            border-color: #ff5722;
            color: #ffffff;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
          }
        `}
`;

const SubmitButton = styled(Button)`
  background: #ff5722 !important;
  border: 2px solid #000000 !important;
  min-width: 140px;
  padding: 0.875rem 2rem;
  color: #fff !important;
  font-family: var(--font-secondary) !important;
  font-weight: 800 !important;
  font-size: 0.9rem !important;
  border-radius: 0 !important;
  transition: transform 0.12s ease, box-shadow 0.12s ease, background 0.15s ease !important;
  box-shadow: 3px 3px 0 #000000 !important;
  position: relative;

  &:disabled {
    background: #475569 !important;
    color: #cbd5e1 !important;
    box-shadow: none !important;
    cursor: not-allowed !important;
    opacity: 0.65 !important;
  }

  &:hover {
    background: #f44e1b !important;
    transform: translate(1px, 1px) !important;
    box-shadow: 2px 2px 0 #000000 !important;
  }

  &:active {
    transform: translateY(0) !important;
  }
`;

const MarketingSubmitButton = styled(SubmitButton)`
  border-radius: 999px !important;
  box-shadow: 0 4px 18px rgba(255, 87, 34, 0.28) !important;
`;

const IntakeBillingIntervalRow = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "$marketing",
})`
  display: flex;
  justify-content: center;
  margin-top: ${(p) => (p.$marketing ? "0.15rem" : "0.35rem")};
  margin-bottom: ${(p) => (p.$marketing ? "0.95rem" : "1.15rem")};
`;

const IntakeIntervalToggle = styled.div`
  display: inline-flex;
  padding: 4px;
  border-radius: 9999px;
  background: ${INTAKE_DARK_CARD};
  box-shadow: none;
  border: 1px solid ${INTAKE_DARK_BORDER};
`;

const IntakeIntervalButton = styled.button`
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.5rem 1.15rem;
  border-radius: 9999px;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: var(--font-secondary, "Inter", sans-serif);
  color: ${INTAKE_DARK_MUTED};
  background: transparent;
  transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;

  ${(p) =>
    p.$active
      ? `
    background: #2c5f2d;
    color: #fff;
    box-shadow: 0 2px 8px rgba(44, 95, 45, 0.3);
  `
      : `
    &:hover {
      color: #e2e8f0;
    }
  `}
`;

const IntakeAnnualIncentiveBadge = styled.span`
  display: inline-block;
  font-size: 0.64rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0.2rem 0.45rem;
  border-radius: 6px;
  background: #cdaf63;
  color: #1f1b14;
  line-height: 1.2;
`;

const PlanToggleWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  width: 100%;
  justify-content: flex-start;
`;

const PlanToggleChip = styled.button`
  flex: 1 1 calc(50% - 0.25rem);
  min-width: min(100%, 9.25rem);
  text-align: left;
  padding: 0.65rem 0.75rem;
  border-radius: 10px;
  border: 2px solid
    ${(p) => (p.$active ? "var(--color-primary)" : INTAKE_DARK_BORDER)};
  background: ${(p) =>
    p.$active ? "rgba(44, 95, 45, 0.2)" : INTAKE_DARK_DEEP};
  box-shadow: ${(p) =>
    p.$active ? "0 0 0 1px rgba(44, 95, 45, 0.22)" : "none"};
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease,
    box-shadow 0.15s ease;

  &:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
  }
`;

const PlanToggleName = styled.div`
  font-family: var(--font-secondary);
  font-weight: 800;
  font-size: 0.95rem;
  letter-spacing: 0.02em;
  color: ${INTAKE_DARK_TEXT};
  margin-bottom: 0.2rem;
`;

const PlanToggleMeta = styled.div`
  font-family: var(--font-secondary);
  font-size: 0.72rem;
  font-weight: 600;
  color: ${INTAKE_DARK_MUTED};
  line-height: 1.35;
`;

// Add styled component for the dropdown row
const PlanBillingRow = styled.div`
  display: flex;
  width: 100%;

  > * {
    flex: 1;
    min-width: 0;
  }

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

// Add styled components for the terms agreement section
const IntakeTermsLabel = styled.span`
  font-size: 0.75rem;
  line-height: 1.35;
  font-weight: 400;
  letter-spacing: 0.01em;

  a {
    font-size: inherit;
    font-weight: 600;
  }
`;

const TermsAgreementContainer = styled.div`
  background: ${INTAKE_DARK_CARD};
  border: 1px solid ${INTAKE_DARK_BORDER};
  border-radius: 0;
  padding: 0.75rem 1rem;
  margin: 0 auto 1.25rem;
  max-width: 100%;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 600px) {
    padding: 0.65rem clamp(0.55rem, 3.5vw, 0.85rem);
    justify-content: center;
    align-items: center;
  }

  && .ant-checkbox-wrapper {
    font-size: 0.75rem !important;
    line-height: 1.35 !important;
    font-weight: 400 !important;
  }

  && .ant-checkbox-wrapper .ant-checkbox + span {
    white-space: nowrap !important;
    font-size: 0.75rem !important;
    line-height: 1.35 !important;
  }

  && .ant-checkbox-wrapper a {
    font-size: 0.75rem !important;
  }

  .ant-form-item {
    margin-bottom: 0 !important;
    width: 100%;
    text-align: center;
  }

  .ant-form-item-control {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ant-form-item-control-input {
    min-height: 0 !important;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
  }

  .ant-form-item-control-input-content {
    min-height: 0 !important;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
  }

  /* Ant Design reserves vertical space for help text; collapse until an error renders */
  .ant-form-item-additional {
    min-height: 0;
  }

  .ant-form-item-has-error .ant-form-item-additional {
    min-height: auto;
  }

  .ant-form-item-explain-error {
    font-size: 0.6875rem;
    text-align: center;
    margin-top: 0.5rem;
    white-space: nowrap;
    @media (max-width: 600px) {
      font-size: 0.625rem !important;
    }
  }

  @media (max-width: 600px) {
    .ant-form-item-control,
    .ant-form-item-control-input,
    .ant-form-item-control-input-content {
      justify-content: center;
    }

    /* Same as IntakeModalWrapGlobal but scoped here: portal + Ant order can skip globals */
    && .ant-checkbox-wrapper {
      display: flex !important;
      align-items: center !important;
      flex-wrap: nowrap !important;
      justify-content: center !important;
      width: auto !important;
      max-width: 100% !important;
      margin-inline: auto !important;
      gap: 0.4rem !important;
      font-size: 0.6875rem !important;
      letter-spacing: 0 !important;
      line-height: 1.35 !important;
    }

    && .ant-checkbox-wrapper .ant-checkbox + span {
      font-size: 0.6875rem !important;
      white-space: nowrap !important;
      flex: 0 1 auto !important;
      min-width: 0 !important;
      text-align: center !important;
      line-height: 1.35 !important;
    }

    && .ant-checkbox-wrapper .ant-checkbox-inner {
      width: 14px !important;
      height: 14px !important;
    }
  }

  @media (max-width: 380px) {
    && .ant-checkbox-wrapper {
      font-size: 0.625rem !important;
      gap: 0.35rem !important;
    }

    && .ant-checkbox-wrapper .ant-checkbox + span {
      font-size: 0.625rem !important;
    }

    && .ant-checkbox-wrapper .ant-checkbox-inner {
      width: 13px !important;
      height: 13px !important;
    }
  }
`;

export default IntakeModal;
